import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'
import UserBar from './user/UserBar'
import media from '@/ui/media'
import SearchBar from './search/SearchBar'
import DateSelectorBar from './booking-filter/DateSelectorBar'
import { RolesEnum, useUserStore } from '@/stores/userStore'
import { OfficeMapIcon } from '@/components/icons'
import { useIntl } from 'react-intl'
import { SectionType } from '@/containers/SidebarContainer'
import { useGlobalStore } from '@/stores/globalStore'
import { useProjectStore } from '@/stores/projectStore'
import BackIcon from '@/components/icons/BackIcon'
import { useTree } from '@/api/hooks/useTree'
import { layerChangeEvent } from '@/components/layout/AppBar/search/SearchItem'
import { useMe } from '@/api/hooks/useMe'
import { act } from 'react-dom/test-utils'
import { BrandLogo } from '@/components/BrandLogo'
import useResponsive from '@/hooks/useResponsive'

const Appbar: React.FC = () => {
  const role = useUserStore((state) => state.role)
  const isAdmin = role === RolesEnum.Admin
  const { isDesktop } = useResponsive()

  return (
    <Wrapper $isAdmin={isAdmin}>
      <LeftGroup>
        {isAdmin ? (
          <NavSlot>
            <BackItem name="Вернуться" icon={BackIcon} />
          </NavSlot>
        ) : (
          <MenuWrapper>
            <LayerItem
              name="План офиса"
              selector="layers"
              icon={OfficeMapIcon}
            />
            <BackItem name="Вернуться" icon={BackIcon} />
          </MenuWrapper>
        )}
        {isDesktop && (
          <LogoSlot>
            <BrandLogo clickable />
          </LogoSlot>
        )}
        <SearchSlot>
          <SearchBar />
        </SearchSlot>
      </LeftGroup>
      {isAdmin && (
        <CenterSlot>
          <DateSelectorBar />
        </CenterSlot>
      )}
      <UserSlot $isAdmin={isAdmin}>
        <UserBar />
      </UserSlot>
    </Wrapper>
  )
}

export default Appbar

const LeftGroup = styled.div`
  grid-column: 1;
  justify-self: start;
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  width: 100%;
`

const NavSlot = styled.div`
  flex-shrink: 0;
`

const LogoSlot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 40px;
`

const SearchSlot = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 420px;
  margin-left: 16px;

  & > * {
    width: 100%;
    max-width: 100%;
  }
`

const CenterSlot = styled.div`
  grid-column: 2;
  justify-self: center;
  flex-shrink: 0;
`

const UserSlot = styled.div<{ $isAdmin: boolean }>`
  grid-column: ${({ $isAdmin }) => ($isAdmin ? 3 : 2)};
  justify-self: end;
  flex-shrink: 0;
`

const MenuWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  flex-shrink: 0;
`

const BackItem = ({ name, icon: Icon }) => {
  const { layers } = useTree()
  const { activeLayer, setActiveLayer } = useGlobalStore()
  const { nodes } = layers || {}
  const current = useMemo(
    () => nodes?.find((node) => node.id == activeLayer),
    [nodes, activeLayer]
  )

  const handleLayerChange = () => {
    if (!current || !current.parent) return
    document.dispatchEvent(layerChangeEvent(current.parent))

    setActiveLayer(Number(current.parent))
  }

  if (!current || !current.parent) return null

  return (
    <Item onClick={handleLayerChange}>
      <Icon />
      <p>{name}</p>
    </Item>
  )
}

interface MenuItemProps {
  name: string
  selector: SectionType
  icon: React.ElementType
}

const LayerItem: React.FC<MenuItemProps> = ({ name, selector, icon: Icon }) => {
  const stateSelector = useGlobalStore((state) => state.selector)
  const setSelector = useGlobalStore((state) => state.setSelector)

  const isActive = selector === stateSelector
  const onChange = () => setSelector(isActive ? null : selector)

  return (
    <Item onClick={onChange}>
      <Icon />
      <p>{name}</p>
    </Item>
  )
}

const Item = styled.div`
  //width: 100%;
  height: 75px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  cursor: pointer;
  //padding: 2rem 1rem;
  position: relative;
  transition: background 0.3s, color 0.3s;

  p {
    font-weight: 400;
    font-size: 10px;
    line-height: 16px;
    text-align: center;
    margin-top: 0.8rem;
  }

  .hover-icon,
  .hover-color,
  .hover-alt,
  .hover-fill {
    transition: all 0.3s;
  }

  img {
    z-index: 2;
    user-select: none;
  }

  color: var(--primary);

  &:hover {
    background: var(--additional);
    color: var(--primary);
  }

  .hover-icon {
    fill: transparent;
  }

  .hover-fill {
    fill: currentColor;
  }

  .hover-color {
    fill: currentColor;
    stroke: currentColor;
  }

  .hover-alt {
    fill: currentColor;
    stroke: var(--surface);
  }
`

const Wrapper = styled.div<{ $isAdmin: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: grid;
  align-items: center;
  grid-template-columns: ${({ $isAdmin }) =>
    $isAdmin ? '1fr auto 1fr' : '1fr auto'};
  column-gap: 16px;
  padding: 0 20px;
  height: 75px;
  border-bottom: 1px solid var(--stroke-border);
  z-index: 2001;
  background: var(--bg-primary);
  color: var(--text-on-color);

  ${media.lg`
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: 16px;
        padding: 0 10px;
        border-radius: 0px;
    `}
`
